/**
 * AgentLogin — Andy's sign-in page.
 *
 * Agent accounts are provisioned manually in Supabase Auth (Dashboard →
 * Authentication → Users → Add user) — no self-signup, no public link to
 * this page. Email + password only.
 *
 * On successful sign-in, navigates back to whatever auth-guarded page the
 * user originally tried to visit (passed via location.state.from by
 * RequireAuth), defaulting to /agent.
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';

export default function AgentLogin() {
  const { user, loading, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const from = location.state?.from?.pathname || '/agent';

  // Already signed in — bounce straight to the dashboard.
  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed. Check your email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Agent Sign In — iProtectYou.Family</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main
        style={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1.5rem',
        }}
      >
        <form
          onSubmit={handleSubmit}
          aria-labelledby="agent-login-heading"
          style={{
            width: '100%',
            maxWidth: '420px',
            padding: '2.5rem 2rem',
            background: '#FFFFFF',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <h1
            id="agent-login-heading"
            style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.5rem' }}
          >
            Agent Sign In
          </h1>
          <p
            style={{
              margin: 0,
              marginBottom: '1.5rem',
              fontSize: '0.95rem',
              color: 'rgba(0,0,0,0.6)',
            }}
          >
            Andy only. Looking for a quote?{' '}
            <a href="https://iprotectyou.family">Head over here</a>.
          </p>

          <label
            htmlFor="email"
            style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}
          >
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            disabled={submitting}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />

          <label
            htmlFor="password"
            style={{
              display: 'block',
              marginTop: '1rem',
              marginBottom: '0.35rem',
              fontWeight: 600,
            }}
          >
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            disabled={submitting}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />

          {error ? (
            <div
              role="alert"
              data-testid="login-error"
              style={{
                marginTop: '1rem',
                padding: '0.65rem 0.85rem',
                background: '#FEF2F2',
                color: '#9F1239',
                borderRadius: '8px',
                fontSize: '0.9rem',
              }}
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting || !email || !password}
            data-testid="login-submit"
            style={{
              marginTop: '1.5rem',
              width: '100%',
              padding: '0.85rem 1rem',
              background: '#1F3A5F',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting || !email || !password ? 0.7 : 1,
            }}
          >
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </main>
    </>
  );
}

const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  fontSize: '1rem',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: '8px',
  boxSizing: 'border-box',
};
